import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from './Layout';
import api, { leadsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { LeadStage, LeadSource, User, FormTemplate } from '../types/Lead';
import { CustomerSegment } from '../types/CustomerSegment';
import { referenceApi } from '../services/referenceApi';
import { customerSegmentApi } from '../services/customerSegmentApi';
import SegmentChangeWarningModal from './SegmentChangeWarningModal';
import { 
  fromApiDate, 
  toApiDate, 
  validateDate, 
  validateDateRelationship, 
  getTodayDate 
} from '../utils/dateUtils';
import { XMarkIcon, EnvelopeIcon } from '@heroicons/react/24/outline';



const LeadForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { currentBranch, isViewingAllBranches } = useBranch();
  const isEdit = Boolean(id);

  // Form data interface (similar to Lead but optimized for form inputs)
  interface FormData {
    organizationId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    alternativeMobileNumber?: string;
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    dateOfBirth?: string;
    company?: string;
    jobTitle?: string;
    streetAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    segmentIds: number[];  // NEW: Customer segments
    stageId: number | null;
    sourceId: number | null;
    assignedTo: number | null;
    leadOwnerId: number | null;
    leadValue: string; // Keep as string for form input
    priority: 'low' | 'medium' | 'high' | 'urgent';
    leadDate: string;
    followUpDate?: string;
    closeDate?: string;
    website?: string;
    linkedinUrl?: string;
    industry?: string;
    companySize?: string;
    notes?: string;
    tags: string[];
    referenceId: number | null;
    referenceCommission: number;
  }

  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [emailPreview, setEmailPreview] = useState<{subject: string; body: string; to: string; templateId: number} | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    organizationId: 0, // Will be set from user context in useEffect
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternativeMobileNumber: '',
    gender: undefined,
    dateOfBirth: '',
    company: '',
    jobTitle: '',
    streetAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    segmentIds: [],  // NEW: Customer segments
    stageId: null,
    sourceId: null,
    assignedTo: null,
    leadOwnerId: null,
    leadValue: '',
    priority: 'medium',
    leadDate: getTodayDate(),
    followUpDate: '',
    closeDate: '',
    website: '',
    linkedinUrl: '',
    industry: '',
    companySize: '',
    notes: '',
    tags: [],
    referenceId: null,
    referenceCommission: 0
  });

  const [stages, setStages] = useState<LeadStage[]>([]);
  const [allStages, setAllStages] = useState<LeadStage[]>([]); // All stages (for reference)
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [sources, setSources] = useState<LeadSource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [references, setReferences] = useState<any[]>([]);
  const [referenceSearchTerm, setReferenceSearchTerm] = useState('');
  const [filteredReferences, setFilteredReferences] = useState<any[]>([]);
  const [showReferenceDropdown, setShowReferenceDropdown] = useState(false);
  const [showSegmentChangeWarning, setShowSegmentChangeWarning] = useState(false);
  const [pendingSegmentChange, setPendingSegmentChange] = useState<{
    segmentIds: number[];
    segmentNames: string[];
    currentStageName: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [tagInput, setTagInput] = useState('');

  // Company size options
  const companySizeOptions = [
    'Self-employed',
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  // Industry options
  const industryOptions = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Manufacturing',
    'Retail',
    'Real Estate',
    'Consulting',
    'Marketing',
    'Legal',
    'Non-profit',
    'Government',
    'Other'
  ];

  // Load form data and options
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load all required data in parallel
        const [stagesRes, sourcesRes, usersRes, templatesRes, referencesRes, segmentsRes] = await Promise.all([
          api.get('/leads/stages?withSegments=true'), // Load all stages with segment info
          api.get('/leads/sources'),
          api.get('/user/all'),
          api.get('/leads/templates'),
          referenceApi.getReferences(),
          customerSegmentApi.getSegments(false) // Load active segments only
        ]);

        // Handle API responses with proper structure
        const stagesData = stagesRes.data?.data || stagesRes.data || [];
        const sourcesData = sourcesRes.data?.data || sourcesRes.data || [];
        const usersData = usersRes.data?.users || usersRes.data?.data || usersRes.data || [];
        const templatesData = templatesRes.data?.data || templatesRes.data || [];
        const referencesData = referencesRes.success ? referencesRes.data : [];
        const segmentsData = segmentsRes.success ? segmentsRes.data : [];

        // Store all stages for reference
        setAllStages(Array.isArray(stagesData) ? stagesData.filter((stage: LeadStage) => stage.isActive !== false) : []);
        
        // Initially show all stages (will be filtered when segments are selected)
        setStages(Array.isArray(stagesData) ? stagesData.filter((stage: LeadStage) => stage.isActive !== false) : []);
        setSources(Array.isArray(sourcesData) ? sourcesData.filter((source: LeadSource) => source.isActive !== false) : []);
        setUsers(Array.isArray(usersData) ? usersData : []);
        setTemplates(Array.isArray(templatesData) ? templatesData.filter((template: FormTemplate) => template.isActive !== false) : []);
        setReferences(Array.isArray(referencesData) ? referencesData : []);
        setFilteredReferences(Array.isArray(referencesData) ? referencesData : []);
        setSegments(Array.isArray(segmentsData) ? segmentsData : []);

        // Set defaults if creating new lead
        if (!isEdit) {
          const defaultStage = stagesData.find((stage: LeadStage) => stage.isDefault);
          const defaultSource = sourcesData.find((source: LeadSource) => source.isDefault);
          const defaultTemplate = templatesData.find((template: FormTemplate) => template.isDefault);
          const defaultSegment = segmentsData.find((segment: CustomerSegment) => segment.isDefault);
          
          setFormData(prev => ({
            ...prev,
            stageId: defaultStage?.id || null,
            sourceId: defaultSource?.id || null,
            segmentIds: defaultSegment ? [defaultSegment.id] : [], // Pre-select default segment
            leadOwnerId: user?.id ? parseInt(user.id) : null // Pre-fill with logged-in user
          }));
          
          if (defaultTemplate) {
            setSelectedTemplate(defaultTemplate);
          } else if (templatesData.length > 0) {
            // If no default template, use the first available template
            setSelectedTemplate(templatesData[0]);
          }
        }

        // Load existing lead if editing
        if (isEdit && id) {
          try {
            const leadRes = await api.get(`/leads/${id}`);
            const lead = leadRes.data?.data || leadRes.data;
          
          setFormData({
            organizationId: lead.organizationId || 0,
            firstName: lead.firstName || '',
            lastName: lead.lastName || '',
            email: lead.email || '',
            phone: lead.phone || '',
            alternativeMobileNumber: lead.alternativeMobileNumber || '',
            gender: lead.gender,
            dateOfBirth: fromApiDate({ date: lead.dateOfBirth }) || '',
            company: lead.company || '',
            jobTitle: lead.jobTitle || '',
            streetAddress: lead.streetAddress || '',
            addressLine1: lead.addressLine1 || '',
            addressLine2: lead.addressLine2 || '',
            city: lead.city || '',
            state: lead.state || '',
            postalCode: lead.postalCode || '',
            country: lead.country || '',
            segmentIds: lead.segmentIds || [],  // NEW: Load segments
            stageId: lead.stageId,
            sourceId: lead.sourceId,
            assignedTo: lead.assignedTo,
            leadOwnerId: lead.leadOwnerId,
            leadValue: lead.leadValue?.toString() || '',
            priority: lead.priority || 'medium',
            leadDate: fromApiDate({ date: lead.leadDate }) || getTodayDate(),
            followUpDate: fromApiDate({ date: lead.followUpDate }) || '',
            closeDate: fromApiDate({ date: lead.closeDate }) || '',
            website: lead.website || '',
            linkedinUrl: lead.linkedinUrl || '',
            industry: lead.industry || '',
            companySize: lead.companySize || '',
            notes: lead.notes || '',
            tags: lead.tags || [],
            referenceId: lead.referenceId || null,
            referenceCommission: lead.referenceCommission || 0
          });
          
          // Reference name will be set in useEffect after references are loaded
          } catch (leadError: any) {
            console.error('Error loading lead:', leadError);
            setError('Failed to load lead data. The lead may not exist or you may not have permission to view it.');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load required data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEdit, id, currentBranch]);

  // Set organization ID from user context when available
  useEffect(() => {
    if (user?.organizationId && formData.organizationId === 0) {
      setFormData(prev => ({
        ...prev,
        organizationId: parseInt((user.organizationId || 0).toString())
      }));
    }
  }, [user?.organizationId, formData.organizationId]);

  // Load filtered stages when segments change
  useEffect(() => {
    const loadFilteredStages = async () => {
      if (formData.segmentIds.length === 0) {
        // No segments selected - show only global stages
        const globalStages = allStages.filter(stage => stage.isGlobal);
        setStages(globalStages);
      } else {
        // Load stages for selected segments
        try {
          const segmentIdsParam = formData.segmentIds.join(',');
          const response = await api.get(`/leads/stages?segmentIds=${segmentIdsParam}`);
          if (response.data.success) {
            setStages(response.data.data || []);
          }
        } catch (error) {
          console.error('Error loading filtered stages:', error);
          // Fallback to all stages
          setStages(allStages);
        }
      }
    };

    // Only reload if we have all stages loaded
    if (allStages.length > 0) {
      loadFilteredStages();
    }
  }, [formData.segmentIds, allStages]);

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle segment selection change
  const handleSegmentChange = (segmentId: number, shouldAdd: boolean) => {
    const newSegmentIds = shouldAdd
      ? [...formData.segmentIds, segmentId]
      : formData.segmentIds.filter(id => id !== segmentId);

    // If editing and segments are changing, validate stage compatibility
    if (isEdit && formData.stageId) {
      const currentStage = allStages.find(s => s.id === formData.stageId);
      
      if (currentStage) {
        // Check if current stage will be valid for new segments
        const willBeValid = currentStage.isGlobal || 
          (newSegmentIds.length > 0 && currentStage.segmentIds?.some(sid => newSegmentIds.includes(sid)));

        if (!willBeValid && newSegmentIds.length > 0) {
          // Stage will become invalid - show warning
          const selectedSegmentNames = segments
            .filter(s => newSegmentIds.includes(s.id))
            .map(s => s.name);

          setPendingSegmentChange({
            segmentIds: newSegmentIds,
            segmentNames: selectedSegmentNames,
            currentStageName: currentStage.name
          });
          setShowSegmentChangeWarning(true);
          return; // Don't update segments yet
        }
      }
    }

    // Safe to update segments
    setFormData(prev => ({ ...prev, segmentIds: newSegmentIds }));
  };

  // Handle segment change confirmation with new stage
  const handleSegmentChangeConfirm = (newStageId: number) => {
    if (pendingSegmentChange) {
      setFormData(prev => ({
        ...prev,
        segmentIds: pendingSegmentChange.segmentIds,
        stageId: newStageId
      }));
    }
    setShowSegmentChangeWarning(false);
    setPendingSegmentChange(null);
  };

  // Handle segment change cancellation
  const handleSegmentChangeCancel = () => {
    setShowSegmentChangeWarning(false);
    setPendingSegmentChange(null);
  };

  // Handle reference search
  const handleReferenceSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setReferenceSearchTerm(searchTerm);
    
    if (searchTerm.trim() === '') {
      setFilteredReferences(references);
    } else {
      const filtered = references.filter((ref: any) =>
        ref.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ref.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ref.contactNumber.includes(searchTerm)
      );
      setFilteredReferences(filtered);
    }
  };

  // Handle reference input focus - show all references
  const handleReferenceFocus = () => {
    setShowReferenceDropdown(true);
    setFilteredReferences(references);
  };

  // Handle reference selection
  const handleReferenceSelect = async (reference: any) => {
    setFormData(prev => ({ ...prev, referenceId: reference.id }));
    setReferenceSearchTerm(reference.name);
    setFilteredReferences([]);
    setShowReferenceDropdown(false);

    // Calculate commission based on selected stage
    if (formData.stageId) {
      try {
        const response = await referenceApi.getCommissionForStage(reference.id, formData.stageId);
        if (response.success) {
          setFormData(prev => ({ ...prev, referenceCommission: response.data.commission }));
        }
      } catch (error) {
        console.error('Error fetching commission:', error);
      }
    }
  };

  // Handle clearing reference selection
  const handleClearReference = () => {
    setFormData(prev => ({ ...prev, referenceId: null, referenceCommission: 0 }));
    setReferenceSearchTerm('');
    setShowReferenceDropdown(false);
  };

  // Handle stage change and update commission
  const handleStageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stageId = parseInt(e.target.value);
    setFormData(prev => ({ ...prev, stageId }));

    // Update commission if reference is selected
    if (formData.referenceId && stageId) {
      try {
        const response = await referenceApi.getCommissionForStage(formData.referenceId, stageId);
        if (response.success) {
          setFormData(prev => ({ ...prev, referenceCommission: response.data.commission }));
        }
      } catch (error) {
        console.error('Error fetching commission:', error);
      }
    }
  };

  // Set reference name when both lead data and references are loaded
  useEffect(() => {
    if (isEdit && formData.referenceId && references.length > 0) {
      const selectedReference = references.find((ref: any) => ref.id === formData.referenceId);
      if (selectedReference) {
        setReferenceSearchTerm(selectedReference.name);
      }
    }
  }, [isEdit, formData.referenceId, references]);

  // Close reference dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showReferenceDropdown && !target.closest('.reference-dropdown-container')) {
        setShowReferenceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReferenceDropdown]);

  // Handle tag input
  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Map template field names (snake_case) to form field names (camelCase)
  const mapTemplateFieldToFormField = (templateField: string): keyof FormData => {
    const fieldMapping: { [key: string]: keyof FormData } = {
      // Basic Information
      'first_name': 'firstName',
      'last_name': 'lastName',
      'email': 'email',
      'phone': 'phone',
      'alternative_mobile_number': 'alternativeMobileNumber',
      'gender': 'gender',
      'date_of_birth': 'dateOfBirth',
      'company': 'company',
      'job_title': 'jobTitle',
      
      // Address Information
      'street_address': 'streetAddress',
      'address_line_1': 'addressLine1',
      'address_line_2': 'addressLine2',
      'city': 'city',
      'state': 'state',
      'postal_code': 'postalCode',
      'country': 'country',
      
      // Lead Management
      'stage_id': 'stageId',
      'source_id': 'sourceId',
      'assigned_to': 'assignedTo',
      'lead_owner_id': 'leadOwnerId',
      'lead_value': 'leadValue',
      
      // Timeline
      'lead_date': 'leadDate',
      'follow_up_date': 'followUpDate',
      'close_date': 'closeDate',
      
      // Additional Information
      'website': 'website',
      'linkedin_url': 'linkedinUrl',
      'industry': 'industry',
      'company_size': 'companySize',
      'notes': 'notes',
      'tags': 'tags',
      'priority': 'priority'
    };
    
    return fieldMapping[templateField] || templateField as keyof FormData;
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const config = selectedTemplate?.fieldsConfig?.fields || {};

    // Check required fields based on template
    Object.entries(config).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.visible && fieldConfig.required) {
        const formFieldName = mapTemplateFieldToFormField(fieldName);
        const value = formData[formFieldName];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          newErrors[fieldName] = 'This field is required';
        }
      }
    });

    // Fallback: if no template is selected, require basic fields
    if (!selectedTemplate) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) newErrors.email = 'Email is required';
    }

    // Lead Owner is always required
    if (!formData.leadOwnerId) {
      newErrors.leadOwnerId = 'Lead Owner is required';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // URL validations
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    if (formData.linkedinUrl && !/^https?:\/\/(www\.)?linkedin\.com\//.test(formData.linkedinUrl)) {
      newErrors.linkedinUrl = 'Please enter a valid LinkedIn URL';
    }

    // Date validations using utility functions
    const dateOfBirthValidation = validateDate(formData.dateOfBirth);
    if (!dateOfBirthValidation.isValid) {
      newErrors.dateOfBirth = dateOfBirthValidation.error || 'Invalid date format';
    }

    const leadDateValidation = validateDate(formData.leadDate);
    if (!leadDateValidation.isValid) {
      newErrors.leadDate = leadDateValidation.error || 'Invalid date format';
    }

    const followUpDateValidation = validateDate(formData.followUpDate);
    if (!followUpDateValidation.isValid) {
      newErrors.followUpDate = followUpDateValidation.error || 'Invalid date format';
    }

    const closeDateValidation = validateDate(formData.closeDate);
    if (!closeDateValidation.isValid) {
      newErrors.closeDate = closeDateValidation.error || 'Invalid date format';
    }

    // Date relationship validations
    const followUpRelationship = validateDateRelationship(
      formData.leadDate, 
      formData.followUpDate, 
      'Follow-up date'
    );
    if (!followUpRelationship.isValid) {
      newErrors.followUpDate = followUpRelationship.error || 'Follow-up date cannot be before lead date';
    }

    const closeDateRelationship = validateDateRelationship(
      formData.leadDate, 
      formData.closeDate, 
      'Close date'
    );
    if (!closeDateRelationship.isValid) {
      newErrors.closeDate = closeDateRelationship.error || 'Close date cannot be before lead date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const { organizationId, ...submitDataWithoutOrgId } = formData;
      
      const submitData = {
        ...submitDataWithoutOrgId,
        leadValue: formData.leadValue ? parseFloat(formData.leadValue) : null,
        assignedTo: formData.assignedTo, // Allow null for unassigned leads
        segmentIds: formData.segmentIds, // Include segments
        // Use utility functions for consistent date handling
        leadDate: toApiDate({ date: formData.leadDate }),
        followUpDate: toApiDate({ date: formData.followUpDate }),
        closeDate: toApiDate({ date: formData.closeDate }),
        dateOfBirth: toApiDate({ date: formData.dateOfBirth })
      };

      // organizationId is excluded - backend will use authenticated user's organizationId

      let response;
      if (isEdit) {
        response = await api.put(`/leads/${id}`, submitData);
      } else {
        response = await api.post('/leads', submitData);
      }

      // Handle segment change validation error from backend
      if (!response.data.success && response.data.requiresStageChange) {
        const selectedSegmentNames = segments
          .filter(s => formData.segmentIds.includes(s.id))
          .map(s => s.name);

        const currentStage = allStages.find(s => s.id === formData.stageId);

        setPendingSegmentChange({
          segmentIds: formData.segmentIds,
          segmentNames: selectedSegmentNames,
          currentStageName: currentStage?.name || 'Current Stage'
        });
        setShowSegmentChangeWarning(true);
        setSaving(false);
        return;
      }

      let createdLeadId: number | null = null;
      if (response.data.success && response.data.data) {
        createdLeadId = response.data.data.id;
      }

      if (response.data.success) {
        // Check if email preview is provided (behavior is 'ask')
        if (response.data.emailPreview && !isEdit && createdLeadId) {
          setEmailPreview({
            subject: response.data.emailPreview.subject,
            body: response.data.emailPreview.body,
            to: response.data.emailPreview.to,
            templateId: response.data.emailPreview.templateId,
            leadId: createdLeadId
          } as any);
          setShowEmailPreviewModal(true);
        } else {
          navigate('/leads');
        }
      } else {
        setErrors({ submit: response.data.message || 'Failed to save lead' });
      }
    } catch (error: any) {
      setErrors({ 
        submit: error.response?.data?.message || 'An error occurred while saving the lead' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Get field visibility from template
  const isFieldVisible = (fieldName: string) => {
    if (!selectedTemplate) return true; // Show all fields if no template
    const config = selectedTemplate.fieldsConfig?.fields?.[fieldName];
    return config?.visible !== false;
  };

  const isFieldRequired = (fieldName: string) => {
    if (!selectedTemplate) return false;
    const config = selectedTemplate.fieldsConfig?.fields?.[fieldName];
    return config?.required === true;
  };

  // Get sorted fields for a section based on template order
  const getSortedFieldsForSection = (fieldNames: string[]) => {
    if (!selectedTemplate) return fieldNames;
    
    const config = selectedTemplate.fieldsConfig?.fields || {};
    
    return fieldNames
      .filter(fieldName => isFieldVisible(fieldName))
      .sort((a, b) => {
        const orderA = config[a]?.order || 999;
        const orderB = config[b]?.order || 999;
        return orderA - orderB;
      });
  };

  // Field configuration interface
  interface FieldConfig {
    label: string;
    type: string;
    name: string;
    placeholder?: string;
    options?: { value: string; label: string; }[];
  }

  // Render individual field based on field name
  const renderField = (fieldName: string) => {
    const fieldConfig: { [key: string]: FieldConfig } = {
      first_name: {
        label: 'First Name',
        type: 'text',
        name: 'firstName',
        placeholder: 'Enter first name'
      },
      last_name: {
        label: 'Last Name', 
        type: 'text',
        name: 'lastName',
        placeholder: 'Enter last name'
      },
      email: {
        label: 'Email',
        type: 'email', 
        name: 'email',
        placeholder: 'Enter email address'
      },
      phone: {
        label: 'Phone',
        type: 'tel',
        name: 'phone', 
        placeholder: 'Enter phone number'
      },
      alternativeMobileNumber: {
        label: 'Alternative Mobile Number',
        type: 'tel',
        name: 'alternativeMobileNumber',
        placeholder: 'Enter alternative mobile number'
      },
      gender: {
        label: 'Gender',
        type: 'select',
        name: 'gender',
        options: [
          { value: '', label: 'Select Gender' },
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'other', label: 'Other' },
          { value: 'prefer_not_to_say', label: 'Prefer not to say' }
        ]
      },
      date_of_birth: {
        label: 'Date of Birth',
        type: 'date',
        name: 'dateOfBirth',
        placeholder: 'Select date of birth'
      },
      company: {
        label: 'Company',
        type: 'text',
        name: 'company',
        placeholder: 'Enter company name'
      },
      job_title: {
        label: 'Job Title',
        type: 'text',
        name: 'jobTitle',
        placeholder: 'Enter job title'
      }
    };

    const config = fieldConfig[fieldName as keyof typeof fieldConfig];
    if (!config) return null;

    const formFieldName = config.name as keyof FormData;
    const value = formData[formFieldName] as string;
    const error = errors[config.name];

    return (
      <div key={fieldName}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {config.label} {isFieldRequired(fieldName) && <span className="text-red-500">*</span>}
        </label>
        {config.type === 'select' ? (
          <select
            name={config.name}
            value={value || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {config.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={config.type}
            name={config.name}
            value={value || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder={config.placeholder}
          />
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Edit Lead' : 'Add New Lead'} subtitle="Lead management">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={isEdit ? 'Edit Lead' : 'Add New Lead'} subtitle="Lead management">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.346 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Error Loading Lead</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/leads')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Leads
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEdit ? 'Edit Lead' : 'Add New Lead'} subtitle="Lead management">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Lead' : 'Create New Lead'}
            </h2>
            <p className="text-gray-600 mt-1">
              {isEdit ? 'Update lead information' : 'Add a new lead to your pipeline'}
            </p>
          </div>
          <button
            onClick={() => navigate('/leads')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        {/* Template Selector */}
        {!isEdit && templates.length > 0 && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Form Template
              </label>
              {selectedTemplate && (
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>{Object.values(selectedTemplate.fieldsConfig.fields).filter(f => f.visible).length} fields</span>
                  <span>•</span>
                  <span>{Object.values(selectedTemplate.fieldsConfig.fields).filter(f => f.visible && f.required).length} required</span>
                </div>
              )}
            </div>
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === parseInt(e.target.value));
                setSelectedTemplate(template || null);
                setErrors({}); // Clear errors when changing template
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All fields (no template)</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.isDefault && '(Default)'}
                </option>
              ))}
            </select>
            {selectedTemplate?.description && (
              <p className="mt-2 text-sm text-gray-600">{selectedTemplate.description}</p>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lead Management */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Management</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Customer Segment */}
              {isFieldVisible('segment_ids') && segments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Segment(s) {isFieldRequired('segment_ids') && <span className="text-red-500">*</span>}
                  </label>
                  
                  {/* Selected Segments as Badges */}
                  {formData.segmentIds.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {formData.segmentIds.map(segId => {
                        const segment = segments.find(s => s.id === segId);
                        if (!segment) return null;
                        return (
                          <span
                            key={segId}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: segment.color }}
                          >
                            {segment.code}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  segmentIds: prev.segmentIds.filter(id => id !== segId)
                                }));
                              }}
                              className="ml-1 text-white hover:text-gray-200"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Dropdown to add segments */}
                  <select
                    value=""
                    onChange={(e) => {
                      const segmentId = parseInt(e.target.value);
                      if (segmentId && !formData.segmentIds.includes(segmentId)) {
                        handleSegmentChange(segmentId, true);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      {formData.segmentIds.length === 0 ? 'Select segment' : 'Add another segment'}
                    </option>
                    {segments
                      .filter(seg => !formData.segmentIds.includes(seg.id))
                      .map(segment => (
                        <option key={segment.id} value={segment.id}>
                          {segment.name} {segment.isDefault && '(Default)'}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              {/* Stage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stage {formData.segmentIds.length > 0 && <span className="text-xs text-gray-500">(Filtered by segment)</span>}
                </label>
                <select
                  name="stageId"
                  value={formData.stageId || ''}
                  onChange={handleStageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a stage</option>
                  
                  {/* Group stages by global vs segment-specific */}
                  {stages.filter(s => s.isGlobal).length > 0 && (
                    <optgroup label="Global Stages">
                      {stages.filter(s => s.isGlobal).map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {stages.filter(s => !s.isGlobal).length > 0 && (
                    <optgroup label="Segment-Specific Stages">
                      {stages.filter(s => !s.isGlobal).map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name} {stage.segmentCodes && `(${stage.segmentCodes})`}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {formData.segmentIds.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Select a segment to see available stages
                  </p>
                )}
              </div>

              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <select
                  name="sourceId"
                  value={formData.sourceId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, sourceId: e.target.value ? parseInt(e.target.value) : null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a source</option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference
                </label>
                <div className="relative reference-dropdown-container">
                  <input
                    type="text"
                    value={referenceSearchTerm}
                    onChange={handleReferenceSearch}
                    onFocus={handleReferenceFocus}
                    placeholder="Click to search references..."
                    className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {/* Clear button */}
                  {referenceSearchTerm && (
                    <button
                      type="button"
                      onClick={handleClearReference}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {/* Dropdown arrow */}
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {showReferenceDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredReferences.length > 0 ? (
                        filteredReferences.map((ref: any) => (
                          <div
                            key={ref.id}
                            onClick={() => handleReferenceSelect(ref)}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{ref.name}</div>
                            <div className="text-sm text-gray-500">{ref.email} • {ref.contactNumber}</div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-gray-500 text-center">
                          No references found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value ? parseInt(e.target.value) : null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lead Owner (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Owner <span className="text-red-500">*</span>
                </label>
                <select
                  name="leadOwnerId"
                  value={formData.leadOwnerId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, leadOwnerId: e.target.value ? parseInt(e.target.value) : null }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.leadOwnerId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select Lead Owner</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.fullName}
                    </option>
                  ))}
                </select>
                {errors.leadOwnerId && (
                  <p className="mt-1 text-sm text-red-600">{errors.leadOwnerId}</p>
                )}
              </div>

              {/* Lead Value */}
              {isFieldVisible('lead_value') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Value ($) {isFieldRequired('lead_value') && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    name="leadValue"
                    value={formData.leadValue}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.leadValue ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.leadValue && (
                    <p className="mt-1 text-sm text-red-600">{errors.leadValue}</p>
                  )}
                </div>
              )}

              {/* Priority */}
              {isFieldVisible('priority') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority {isFieldRequired('priority') && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.priority ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  {errors.priority && (
                    <p className="mt-1 text-sm text-red-600">{errors.priority}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Basic Information */}
          {(() => {
            const basicFields = ['first_name', 'last_name', 'email', 'phone', 'alternative_mobile_number', 'gender', 'date_of_birth', 'company', 'job_title'];
            const sortedBasicFields = getSortedFieldsForSection(basicFields);
            
            if (sortedBasicFields.length === 0) return null;
            
            return (
              <div className="bg-white p-6 rounded-lg shadow border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sortedBasicFields.map(fieldName => renderField(fieldName))}
                </div>
              </div>
            );
          })()}

          {/* Address Information */}
          {(isFieldVisible('street_address') || isFieldVisible('address_line_1') || isFieldVisible('address_line_2') || isFieldVisible('city') || isFieldVisible('state') || isFieldVisible('postal_code') || isFieldVisible('country')) && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Street Address */}
                {isFieldVisible('street_address') && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address {isFieldRequired('street_address') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.streetAddress ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter street address"
                    />
                    {errors.streetAddress && (
                      <p className="mt-1 text-sm text-red-600">{errors.streetAddress}</p>
                    )}
                  </div>
                )}

                {/* Address Line 1 */}
                {isFieldVisible('address_line_1') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 1 {isFieldRequired('address_line_1') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.addressLine1 ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter address line 1"
                    />
                    {errors.addressLine1 && (
                      <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>
                    )}
                  </div>
                )}

                {/* Address Line 2 */}
                {isFieldVisible('address_line_2') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 2 {isFieldRequired('address_line_2') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.addressLine2 ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter address line 2"
                    />
                    {errors.addressLine2 && (
                      <p className="mt-1 text-sm text-red-600">{errors.addressLine2}</p>
                    )}
                  </div>
                )}

                {/* City */}
                {isFieldVisible('city') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City {isFieldRequired('city') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.city ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter city"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                    )}
                  </div>
                )}

                {/* State */}
                {isFieldVisible('state') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province {isFieldRequired('state') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.state ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter state or province"
                    />
                    {errors.state && (
                      <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                    )}
                  </div>
                )}

                {/* Postal Code */}
                {isFieldVisible('postal_code') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code {isFieldRequired('postal_code') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.postalCode ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter postal code"
                    />
                    {errors.postalCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                    )}
                  </div>
                )}

                {/* Country */}
                {isFieldVisible('country') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country {isFieldRequired('country') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.country ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter country"
                    />
                    {errors.country && (
                      <p className="mt-1 text-sm text-red-600">{errors.country}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          {(isFieldVisible('lead_date') || isFieldVisible('follow_up_date') || isFieldVisible('close_date')) && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Lead Date */}
                {isFieldVisible('lead_date') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead Date {isFieldRequired('lead_date') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="leadDate"
                      value={formData.leadDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.leadDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.leadDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.leadDate}</p>
                    )}
                  </div>
                )}

                {/* Follow-up Date */}
                {isFieldVisible('follow_up_date') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Follow-up Date {isFieldRequired('follow_up_date') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="followUpDate"
                      value={formData.followUpDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.followUpDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.followUpDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.followUpDate}</p>
                    )}
                  </div>
                )}

                {/* Close Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Close Date
                  </label>
                  <input
                    type="date"
                    name="closeDate"
                    value={formData.closeDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.closeDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.closeDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.closeDate}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          {(isFieldVisible('website') || isFieldVisible('linkedin_url') || isFieldVisible('industry') || isFieldVisible('company_size')) && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Website */}
                {isFieldVisible('website') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website {isFieldRequired('website') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.website ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://example.com"
                    />
                    {errors.website && (
                      <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                    )}
                  </div>
                )}

                {/* LinkedIn URL */}
                {isFieldVisible('linkedin_url') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn URL {isFieldRequired('linkedin_url') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="url"
                      name="linkedinUrl"
                      value={formData.linkedinUrl}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.linkedinUrl ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://linkedin.com/in/username"
                    />
                    {errors.linkedinUrl && (
                      <p className="mt-1 text-sm text-red-600">{errors.linkedinUrl}</p>
                    )}
                  </div>
                )}

                {/* Industry */}
                {isFieldVisible('industry') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry {isFieldRequired('industry') && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.industry ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select an industry</option>
                      {industryOptions.map(industry => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                    {errors.industry && (
                      <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
                    )}
                  </div>
                )}

                {/* Company Size */}
                {isFieldVisible('company_size') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Size {isFieldRequired('company_size') && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.companySize ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select company size</option>
                      {companySizeOptions.map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    {errors.companySize && (
                      <p className="mt-1 text-sm text-red-600">{errors.companySize}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes and Tags */}
          {(isFieldVisible('notes') || isFieldVisible('tags')) && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notes & Tags</h3>
              <div className="space-y-4">
                {/* Notes */}
                {isFieldVisible('notes') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes {isFieldRequired('notes') && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.notes ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Add any additional notes about this lead..."
                    />
                    {errors.notes && (
                      <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
                    )}
                  </div>
                )}

                {/* Tags */}
                {isFieldVisible('tags') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags {isFieldRequired('tags') && <span className="text-red-500">*</span>}
                    </label>
                    <div className="space-y-2">
                      {/* Tag Input */}
                      <div className="flex">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={handleTagInputChange}
                          onKeyPress={handleTagInputKeyPress}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Type a tag and press Enter or comma"
                        />
                        <button
                          type="button"
                          onClick={addTag}
                          className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                        >
                          Add
                        </button>
                      </div>
                      
                      {/* Tag Display */}
                      {(formData.tags?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {(formData.tags || []).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {errors.tags && (
                      <p className="mt-1 text-sm text-red-600">{errors.tags}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="bg-white p-6 rounded-lg shadow border">
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/leads')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {saving ? 'Saving...' : (isEdit ? 'Update Lead' : 'Create Lead')}
              </button>
            </div>
          </div>
        </form>

        {/* Email Preview Modal */}
        {showEmailPreviewModal && emailPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Send Welcome Email?</h2>
                </div>
                <button
                  onClick={() => {
                    setShowEmailPreviewModal(false);
                    setEmailPreview(null);
                    navigate('/leads');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">To: <span className="font-medium text-gray-900">{emailPreview.to}</span></p>
                  {(emailPreview as any).templateName && (
                    <p className="text-sm text-gray-600 mb-2">Template: <span className="font-medium text-gray-900">{(emailPreview as any).templateName}</span></p>
                  )}
                  <p className="text-sm text-gray-600 mb-4">Subject: <span className="font-medium text-gray-900">{emailPreview.subject}</span></p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto">
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700">
                    {emailPreview.body}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEmailPreviewModal(false);
                      setEmailPreview(null);
                      navigate('/leads');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={sendingEmail}
                  >
                    Don't Send
                  </button>
                  <button
                    onClick={async () => {
                      setSendingEmail(true);
                      try {
                        const leadId = (emailPreview as any).leadId;
                        await leadsAPI.sendWelcomeEmail(leadId, emailPreview.templateId);
                        setShowEmailPreviewModal(false);
                        setEmailPreview(null);
                        navigate('/leads');
                      } catch (error: any) {
                        setErrors({ submit: error.response?.data?.message || 'Failed to send email' });
                        setSendingEmail(false);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={sendingEmail}
                  >
                    {sendingEmail && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Segment Change Warning Modal */}
        {showSegmentChangeWarning && pendingSegmentChange && (
          <SegmentChangeWarningModal
            isOpen={showSegmentChangeWarning}
            onClose={handleSegmentChangeCancel}
            onConfirm={handleSegmentChangeConfirm}
            currentStageName={pendingSegmentChange.currentStageName}
            newSegmentIds={pendingSegmentChange.segmentIds}
            newSegmentNames={pendingSegmentChange.segmentNames}
          />
        )}
      </div>
    </Layout>
  );
};

export default LeadForm;
